import { Icon } from '../../../components/index.js';
import { resolveCosmeticAsset } from '../../profile/profile-assets.js';
import type { ProfileThemePreset } from '../../profile/profile-assets.js';

/**
 * Visual theme-preset cards (APP_SPEC §13.2). A preset is a convenience that
 * writes three cosmetic fields at once — never a lock — so `selectedId` (see
 * `cosmetic-match.ts`) only lights up a card while the draft still matches
 * every one of its fields exactly; changing one piece afterwards simply
 * un-highlights every card rather than showing a false/partial match.
 *
 * Thumbnails reuse each preset's own hero-background art (already
 * landscape-shaped) — no separate preset-thumbnail asset exists or is
 * needed.
 */
export interface ThemePresetSelectorProps {
  presets: readonly ProfileThemePreset[];
  selectedId: string | null;
  onSelect: (presetId: string) => void;
}

export function ThemePresetSelector({ presets, selectedId, onSelect }: ThemePresetSelectorProps) {
  return (
    <div
      className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-1 tablet:mx-0 tablet:grid tablet:grid-cols-3 tablet:gap-4 tablet:overflow-visible tablet:px-0 tablet:pb-0 wide:grid-cols-5"
      role="group"
      aria-label="Theme preset"
    >
      {presets.map((preset) => {
        const selected = preset.id === selectedId;
        return (
          <button
            key={preset.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(preset.id)}
            className={`interactive-lift relative w-24 shrink-0 snap-start overflow-hidden rounded-md border bg-surface text-left transition-colors duration-fast ease-standard tablet:w-auto tablet:shrink ${
              selected
                ? 'border-accent bg-brand/10 shadow-1'
                : 'border-border hover:border-borderStrong'
            }`}
          >
            <span className="block aspect-[4/3] w-full overflow-hidden bg-inset">
              <img
                src={resolveCosmeticAsset('heroBackground', preset.heroBackgroundId)}
                alt=""
                aria-hidden="true"
                draggable={false}
                className="h-full w-full object-cover"
              />
            </span>
            {selected && (
              <span
                aria-hidden="true"
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border border-accent bg-brand text-textOnBrand shadow-1"
              >
                <Icon name="check" className="h-3 w-3" />
              </span>
            )}
            <span className="block truncate px-1.5 py-1.5 text-xs font-semibold text-textPrimary">
              {preset.name}
              {selected && <span className="sr-only"> (selected)</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
